import React from 'react';
import { 
  Search, MessageSquare, Clock, ChevronUp, ChevronDown, 
  Filter, SortAsc, PlusCircle, ArrowRight, Eye, ThumbsUp,
  AlertTriangle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ChevronRight from './ChevronRight';
import { useQueryClient } from '@tanstack/react-query';
import { DiscussionWithRelations } from '@/src/queries/community';

interface MainViewProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showFilterDropdown: boolean;
  setShowFilterDropdown: (show: boolean) => void;
  showSortDropdown: boolean;
  setShowSortDropdown: (show: boolean) => void;
  filterOptions: Array<{id: string, name: string}>;
  selectedFilter: string;
  handleFilterSelect: (filterId: string) => void;
  sortOptions: Array<{id: string, name: string}>;
  selectedSort: string;
  handleSortSelect: (sortId: string) => void;
  isLoadingCategories: boolean;
  allCategories: Array<{id: string, icon: React.ReactNode, name: string}>;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  isLoadingDiscussions: boolean;
  isErrorDiscussions: boolean;
  discussions: DiscussionWithRelations[];
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
  handleDiscussionClick: (discussionId: string) => void;
  handleAuthorClick: (e: React.MouseEvent) => void;
  setShowNewDiscussionModal: (show: boolean) => void;
  setCurrentView: (view: string) => void;
  isLoadingPopularTopics: boolean;
  popularTopics: any[];
  setShowVerificationModal: (show: boolean) => void;
}

const MainView: React.FC<MainViewProps> = ({
  searchQuery,
  setSearchQuery,
  showFilterDropdown,
  setShowFilterDropdown,
  showSortDropdown,
  setShowSortDropdown,
  filterOptions,
  selectedFilter,
  handleFilterSelect,
  sortOptions,
  selectedSort,
  handleSortSelect,
  isLoadingCategories,
  allCategories,
  activeCategory,
  setActiveCategory,
  isLoadingDiscussions,
  isErrorDiscussions,
  discussions,
  totalPages,
  currentPage,
  handlePageChange,
  handleDiscussionClick,
  handleAuthorClick,
  setShowNewDiscussionModal,
  setCurrentView,
  isLoadingPopularTopics,
  popularTopics,
  setShowVerificationModal
}) => {
  const queryClient = useQueryClient();

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Main content area - 3/4 width on large screens */}
          <div className="lg:col-span-3">
            {/* Search and filter bar */}
            <div className="bg-white rounded-lg shadow-md mb-6 p-4">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div className="relative flex-grow mb-4 sm:mb-0 sm:mr-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search discussions..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex space-x-2">
                  {/* Filter button with dropdown */}
                  <div className="relative">
                    <button 
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      Filter: {filterOptions.find(option => option.id === selectedFilter)?.name}
                      {showFilterDropdown ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
                    </button>
                    
                    {showFilterDropdown && (
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          {filterOptions.map(option => (
                            <button
                              key={option.id}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                selectedFilter === option.id 
                                  ? 'bg-indigo-100 text-indigo-900' 
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              role="menuitem"
                              onClick={() => handleFilterSelect(option.id)}
                            >
                              {option.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Sort button with dropdown */}
                  <div className="relative">
                    <button 
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
                      onClick={() => setShowSortDropdown(!showSortDropdown)}
                    >
                      <SortAsc className="mr-2 h-4 w-4" />
                      Sort: {sortOptions.find(option => option.id === selectedSort)?.name}
                      {showSortDropdown ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
                    </button>
                    
                    {showSortDropdown && (
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          {sortOptions.map(option => (
                            <button
                              key={option.id}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                selectedSort === option.id 
                                  ? 'bg-indigo-100 text-indigo-900' 
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              role="menuitem"
                              onClick={() => handleSortSelect(option.id)}
                            >
                              {option.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Category tabs */}
            {!isLoadingCategories && (
              <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
                <div className="flex overflow-x-auto hide-scrollbar">
                  {allCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`px-4 py-3 font-medium text-sm flex items-center whitespace-nowrap ${
                        activeCategory === category.id
                          ? 'text-indigo-600 border-b-2 border-indigo-600'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-2">{category.icon}</span>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Loading state */}
            {isLoadingDiscussions && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900">Loading discussions...</h3>
              </div>
            )}
            
            {/* Error state */}
            {isErrorDiscussions && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load discussions</h3>
                <p className="text-gray-600 mb-6">
                  There was an error loading the discussions. Please try again later.
                </p>
                <button 
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['discussions'] })}
                >
                  Retry
                </button>
              </div>
            )}
            
            {/* Discussions list */}
            {!isLoadingDiscussions && !isErrorDiscussions && (
              <div className="space-y-4">
                {discussions.length > 0 ? (
                  discussions.map((discussion) => (
                    <div 
                      key={discussion.id} 
                      className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer ${discussion.is_sticky ? 'border-l-4 border-indigo-600' : ''}`}
                      onClick={() => handleDiscussionClick(discussion.id)}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center mb-2">
                              {discussion.is_sticky && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mr-2">
                                  Pinned
                                </span>
                              )}
                              {discussion.is_solved && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                                  Solved
                                </span>
                              )}
                              {allCategories.find(c => c.id === discussion.category_id)?.icon && (
                                <span className="text-gray-500 mr-2">
                                  {allCategories.find(c => c.id === discussion.category_id)?.icon}
                                </span>
                              )}
                              <h3 className="text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors">
                                {discussion.title}
                              </h3>
                            </div>
                            <p className="text-gray-600 mb-4">{discussion.preview}</p>
                            <div className="flex items-center text-sm text-gray-500">
                              <div className="flex items-center">
                                <div 
                                  className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 font-medium text-xs mr-2 cursor-pointer"
                                  onClick={handleAuthorClick}
                                >
                                  {discussion.user.username?.substring(0, 2).toUpperCase()}
                                </div>
                                <span 
                                  className="font-medium text-gray-900 cursor-pointer hover:text-indigo-600"
                                  onClick={handleAuthorClick}
                                >
                                  {discussion.user.username}
                                </span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  discussion.user.experience_level === 'Beginner' ? 'bg-green-100 text-green-800' :
                                  discussion.user.experience_level === 'Intermediate' ? 'bg-blue-100 text-blue-800' :
                                  discussion.user.experience_level === 'Advanced' ? 'bg-purple-100 text-purple-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {discussion.user.experience_level}
                                </span>
                              </div>
                              <span className="mx-2">•</span>
                              <Clock className="h-4 w-4 mr-1" />
                              <span>{formatDistanceToNow(new Date(discussion.last_activity_at), { addSuffix: true })}</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex flex-col items-end space-y-2">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center text-gray-500">
                                <MessageSquare className="h-4 w-4 mr-1" />
                                <span>{discussion._count?.comments || 0}</span>
                              </div>
                              <div className="flex items-center text-gray-500">
                                <Eye className="h-4 w-4 mr-1" />
                                <span>{discussion.views}</span>
                              </div>
                              <div className="flex items-center text-gray-500">
                                <ThumbsUp className="h-4 w-4 mr-1" />
                                <span>{discussion._count?.likes || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No discussions found</h3>
                    <p className="text-gray-600 mb-6">
                      There are no discussions matching your current filters. Try adjusting your search or create a new discussion.
                    </p>
                    <button 
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors inline-flex items-center"
                      onClick={() => setShowNewDiscussionModal(true)}
                    >
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Start New Discussion
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Pagination */}
            {!isLoadingDiscussions && !isErrorDiscussions && discussions.length > 0 && (
              <div className="mt-8 flex justify-between items-center">
                <button 
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
                <div className="flex space-x-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Show current page in the middle when possible
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`px-4 py-2 ${
                          pageNum === currentPage
                            ? 'bg-indigo-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        } rounded-md`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="px-4 py-2 text-gray-700">...</span>
                      <button
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                <button 
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          
          {/* Sidebar - 1/4 width on large screens */}
          <div className="mt-8 lg:mt-0">
            {/* Community guidelines */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Community Guidelines</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-indigo-600 flex-shrink-0 mr-2" />
                  <span>Be respectful and supportive of fellow students</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-indigo-600 flex-shrink-0 mr-2" />
                  <span>Stay on topic and use appropriate categories</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-indigo-600 flex-shrink-0 mr-2" />
                  <span>No sharing of specific test questions or licensed material</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-indigo-600 flex-shrink-0 mr-2" />
                  <span>Check the FAQ before posting common questions</span>
                </li>
              </ul>
              <button 
                className="mt-4 text-indigo-600 text-sm font-medium hover:text-indigo-700 inline-flex items-center"
                onClick={() => setCurrentView('guidelines')}
              >
                Read full guidelines
                <ArrowRight className="ml-1 h-4 w-4" />
              </button>
            </div>
            
            {/* Popular topics */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Popular Topics</h3>
              {isLoadingPopularTopics ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {popularTopics.map((topic) => (
                    <div 
                      key={topic.id} 
                      className="border-b border-gray-100 pb-4 last:border-0 last:pb-0 cursor-pointer"
                      onClick={() => handleDiscussionClick(topic.id)}
                    >
                      <h4 className="font-medium text-gray-900 hover:text-indigo-600 transition-colors mb-1">
                        {topic.title}
                      </h4>
                      <div className="flex items-center text-sm text-gray-500">
                        <Eye className="h-4 w-4 mr-1" />
                        <span className="mr-3">{topic.views} views</span>
                        <MessageSquare className="h-4 w-4 mr-1" />
                        <span>{topic._count?.comments || 0} replies</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Study buddy finder */}
            {/* <div className="bg-linear-to-r from-indigo-600 to-purple-600 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-white mb-4">Find a Study Buddy</h3>
              <p className="text-indigo-100 mb-4">
                Connect with other students preparing for the same test date to study together and keep each other accountable.
              </p>
              <button 
                className="w-full py-2 bg-white text-indigo-600 font-medium rounded-md hover:bg-indigo-50 transition-colors shadow-md flex items-center justify-center"
                onClick={() => setShowVerificationModal(true)}
              >
                Find Study Partners
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainView;
